import {
  BI_UOU_ENDPOINT,
  BI_UOU_SRC,
  BI_USERS_ENDPOINT,
  BI_USERS_SRC,
} from '@wix/communities-blog-client-common';

import { blogAppDefId } from '../constants/apps';
import createBiMiddleware from '../middleware/bi-middleware';
import { getPreviousMatch, getCurrentMatch } from '../router/router-selectors';
import { getRouteName } from '../services/detect-route';
import {
  getCurrentUserSiteMemberId,
  getCurrentUserRole,
} from '../store/auth/current-user-selectors';

import { isSite } from '../store/basic-params/basic-params-selectors';
import { getBiToken, getInstanceValue } from '../store/instance-values/instance-values-selectors';
import { type AppState, type MatchedRoute } from '../types';

type BiEvent = Record<string, any>;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const hasBiToken = (event: BiEvent) =>
  (event.biToken && event.biToken !== EMPTY_GUID) || event.msid;

const getEventOrigin = ({ state, event }: { state: AppState; event: BiEvent }) => {
  const routeMatch = (
    event.eventMeta && event.eventMeta.isNavigation
      ? getPreviousMatch(state)
      : getCurrentMatch(state)
  ) as MatchedRoute;
  return getRouteName(routeMatch.route);
};

const extendAppUouEvent = (eventDefaults: BiEvent) => (rawEvent: BiEvent, state: AppState) => {
  const event = { ...eventDefaults, ...rawEvent };
  return {
    biToken: getBiToken(state),
    origin: event.origin || getEventOrigin({ event, state }),
    role: getCurrentUserRole(state),
    site_member_id: getCurrentUserSiteMemberId(state),
    visitor_id: getInstanceValue(state, 'aid') || getInstanceValue(state, 'uid'),
    ...event,
  };
};

const extendAppUsersEvent = (eventDefaults: BiEvent) => (rawEvent: BiEvent, state: AppState) => {
  const event = { ...eventDefaults, ...rawEvent };

  return {
    biToken: getBiToken(state),
    role: 'owner',
    origin: event.origin,
    ...event,
  };
};

const createBiInstance = (biLoggerFactory: () => any) => {
  const bi = biLoggerFactory();
  return bi.factory ? bi.factory() : bi;
};

type MiddlewareParams = {
  platformAPIs: any;
  instanceId: string;
  eventMap: Record<string, (...args: any[]) => any>;
  eventDefaults?: BiEvent;
  shouldInvokeEvent?: (evid: unknown, state: AppState) => boolean;
};

export const appUouBiMiddleware = ({
  platformAPIs,
  instanceId,
  eventMap,
  eventDefaults = {},
}: MiddlewareParams) => {
  const bi = createBiInstance(platformAPIs.biLoggerFactory);
  bi.updateDefaults({
    src: BI_UOU_SRC,
    app_id: blogAppDefId,
    app_site_id: instanceId,
    instance_id: instanceId,
    referral_info: undefined,
  });

  return createBiMiddleware({
    bi: bi.logger({ endpoint: BI_UOU_ENDPOINT }),
    eventMap,
    extendEvent: extendAppUouEvent(eventDefaults),
    shouldInvokeEvent: (_: unknown, state: AppState) => isSite(state),
    shouldLogEvent: hasBiToken,
  } as any);
};

export const appUsersBiMiddleware = ({
  platformAPIs,
  instanceId,
  eventMap,
  eventDefaults = {},
  shouldInvokeEvent = (_: unknown, state: AppState) => !isSite(state),
}: MiddlewareParams) => {
  const bi = createBiInstance(platformAPIs.biLoggerFactory);
  bi.updateDefaults({
    src: BI_USERS_SRC,
    app_id: blogAppDefId,
    community_id: undefined,
    instance_id: instanceId,
    original_instance_id: undefined,
    referral_info: undefined,
  });

  return createBiMiddleware({
    bi: bi.logger({ endpoint: BI_USERS_ENDPOINT }),
    eventMap,
    extendEvent: extendAppUsersEvent(eventDefaults),
    shouldInvokeEvent,
    shouldLogEvent: hasBiToken,
  } as any);
};
